import type { GraphQLFormattedError } from 'graphql';

import { CONFIG } from '@/config';
import { STATUS_CODES } from '@/constants/common';

const { AUTHENTICATION_ERROR, INVALID_CREDENTIALS } = STATUS_CODES;

/**
 * Array of error status codes that should be hidden
 */
const HIDDEN_ERROR_STATUSES = [
  AUTHENTICATION_ERROR,
  INVALID_CREDENTIALS
] as const;

/**
 * Checks if the GraphQL error should be hidden
 * Hides errors with status codes from HIDDEN_ERROR_STATUSES
 */
function shouldHideError(error: GraphQLFormattedError): boolean {
  const errorCode = error.extensions?.code;
  const normalizedMessage = error.message.toLowerCase();
  const errorPath = error.path ?? [];

  // In local/dev some unauthenticated requests are expected.
  // Skip noisy AUTHENTICATION_ERROR logs for these specific cases.
  const isExpectedLocalAuthPermissionError =
    !CONFIG.isProduction &&
    errorCode === AUTHENTICATION_ERROR &&
    normalizedMessage.includes('permission denied') &&
    (errorPath.includes('profile') || errorPath.includes('paidServices'));

  if (isExpectedLocalAuthPermissionError) {
    return true;
  }

  if (!CONFIG.isProduction) {
    return false;
  }

  const isHiddenStatus = HIDDEN_ERROR_STATUSES.includes(
    errorCode as (typeof HIDDEN_ERROR_STATUSES)[number]
  );

  return isHiddenStatus;
}

/**
 * Formats GraphQL error for logging
 */
function formatGraphQLError(error: GraphQLFormattedError): string {
  const parts: string[] = [];

  if (error.extensions?.code) {
    parts.push(`Status: ${error.extensions.code}`);
  }

  if (error.locations && error.locations.length > 0) {
    parts.push(`Location: ${JSON.stringify(error.locations)}`);
  }

  if (error.path && error.path.length > 0) {
    parts.push(`Path: ${error.path.join('.')}`);
  }

  if (error.message) {
    parts.push(`Message: ${error.message}`);
  }

  return parts.join('; ');
}

/**
 * Logs GraphQL errors, filtering out hidden errors
 */
export function logGraphQLErrors(
  errors: readonly GraphQLFormattedError[]
): void {
  const visibleErrors = errors.filter(error => !shouldHideError(error));

  if (visibleErrors.length === 0) {
    return;
  }

  visibleErrors.forEach(error => {
    const formattedError = formatGraphQLError(error);
    console.error(`[GraphQL error]: ${formattedError}`);
  });
}
